import { Injectable, OnDestroy, signal } from '@angular/core';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService implements OnDestroy {
  private readonly idleTimeoutMs = 5 * 60 * 1000;
  private readonly warningDurationMs = 60 * 1000;
  private readonly refreshMinValiditySeconds = 60;
  private readonly refreshTickMs = 30 * 1000;

  private readonly activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
  private readonly boundActivityHandler = () => this.handleActivity();

  private lastActivityAt = Date.now();
  private warningCountdownSeconds = 0;
  private checkIntervalId: ReturnType<typeof setInterval> | null = null;
  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;
  private refreshIntervalId: ReturnType<typeof setInterval> | null = null;
  private started = false;
  private handlingExpiry = false;
  private awaitingConfirmation = false;

  protected readonly warningVisibleState = signal(false);
  protected readonly warningSecondsLeftState = signal(0);

  constructor(private readonly authService: AuthService) {}

  get warningVisible(): boolean {
    return this.warningVisibleState();
  }

  get warningSecondsLeft(): number {
    return this.warningSecondsLeftState();
  }

  start(): void {
    if (this.started || !this.authService.authenticated) {
      return;
    }

    this.started = true;
    this.lastActivityAt = Date.now();
    this.registerActivityListeners();

    this.checkIntervalId = setInterval(() => {
      void this.evaluateTimeoutState();
    }, 1000);

    this.refreshIntervalId = setInterval(() => {
      void this.refreshTokenIfNeeded();
    }, this.refreshTickMs);
  }

  stop(): void {
    this.started = false;
    this.unregisterActivityListeners();
    this.clearIntervals();
    this.hideWarning();
  }

  async extendSession(): Promise<void> {
    try {
      await this.authService.updateToken(this.refreshMinValiditySeconds);
      this.markActivity();
      this.hideWarning();
    } catch {
      await this.handleSessionExpiry();
    }
  }

  async logoutNow(): Promise<void> {
    this.stop();
    await this.authService.logout();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private registerActivityListeners(): void {
    for (const eventName of this.activityEvents) {
      window.addEventListener(eventName, this.boundActivityHandler, { passive: true });
    }
  }

  private unregisterActivityListeners(): void {
    for (const eventName of this.activityEvents) {
      window.removeEventListener(eventName, this.boundActivityHandler);
    }
  }

  private handleActivity(): void {
    if (this.awaitingConfirmation) {
      return;
    }
    this.markActivity();
  }

  private markActivity(): void {
    this.lastActivityAt = Date.now();
  }

  private async evaluateTimeoutState(): Promise<void> {
    if (!this.authService.authenticated) {
      return;
    }

    const now = Date.now();
    const idleElapsedMs = now - this.lastActivityAt;
    const idleRemainingMs = this.idleTimeoutMs - idleElapsedMs;

    if (idleRemainingMs <= 0) {
      await this.handleSessionExpiry();
      return;
    }

    if (idleRemainingMs <= this.warningDurationMs) {
      this.showWarning(Math.ceil(idleRemainingMs / 1000));
    } else if (this.warningVisibleState()) {
      this.hideWarning();
    }
  }

  private async refreshTokenIfNeeded(): Promise<void> {
    if (!this.authService.authenticated || this.warningVisibleState()) {
      return;
    }

    const idleElapsedMs = Date.now() - this.lastActivityAt;
    const isRecentlyActive = idleElapsedMs < this.idleTimeoutMs;
    if (!isRecentlyActive) {
      return;
    }

    try {
      await this.authService.updateToken(this.refreshMinValiditySeconds);
    } catch {
      await this.handleSessionExpiry();
    }
  }

  private showWarning(secondsLeft: number): void {
    this.awaitingConfirmation = true;
    this.warningCountdownSeconds = Math.max(1, secondsLeft);
    this.warningSecondsLeftState.set(this.warningCountdownSeconds);
    this.warningVisibleState.set(true);

    if (this.countdownIntervalId) {
      return;
    }

    this.countdownIntervalId = setInterval(() => {
      this.warningCountdownSeconds = Math.max(0, this.warningCountdownSeconds - 1);
      this.warningSecondsLeftState.set(this.warningCountdownSeconds);
    }, 1000);
  }

  private hideWarning(): void {
    this.awaitingConfirmation = false;
    this.warningVisibleState.set(false);
    this.warningSecondsLeftState.set(0);
    this.warningCountdownSeconds = 0;

    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  }

  private async handleSessionExpiry(): Promise<void> {
    if (this.handlingExpiry) {
      return;
    }

    this.handlingExpiry = true;
    this.stop();
    await this.authService.logout();
  }

  private clearIntervals(): void {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  }
}
